import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAccess, useTenantDetail } from "@/lib/hooks/use-admin";
import { useMe } from "@/lib/hooks/use-clients";
import { TenantUsersWrapper } from "./tenant-users-wrapper";

export default function TenantUsersPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { me, isLoading: meLoading } = useMe();
  const { tenant, isLoading: tenantLoading } = useTenantDetail(id ?? null);

  const { isLoading: accessLoading, canViewAdmin } = useAdminAccess();

  const isLoading = meLoading || accessLoading || tenantLoading;

  useEffect(() => {
    if (isLoading) return;
    if (!me || !canViewAdmin) {
      navigate("/", { replace: true });
      return;
    }
    if (!tenant) {
      navigate("/", { replace: true });
    }
  }, [isLoading, me, canViewAdmin, tenant, navigate]);

  if (isLoading || !me || !canViewAdmin || !tenant) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const isQuantfolio = tenant.isQuantfolio;

  const breadcrumbItems = isQuantfolio
    ? [
        { label: "Admin", href: "/admin" },
        { label: "Tenants", href: "/tenants" },
        { label: tenant.name, href: `/tenants/${tenant.id}` },
        { label: "Users" },
      ]
    : [
        { label: "Admin", href: "/admin" },
        { label: "Tenants", href: "/admin/tenants" },
        { label: tenant.name, href: `/tenants/${tenant.id}` },
        { label: "Users" },
      ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={breadcrumbItems}
        title={`Users - ${tenant.name}`}
        description="Manage users for this tenant"
      />

      <TenantUsersWrapper tenantId={tenant.id} />
    </div>
  );
}
